/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / DEALS-ADMIN — Deals-Verwaltung (S169 / Teil A · P2)
   =============================================================================
   Cockpit-Subtab „Deals" unter „System" (neben Tischplan/Speisekarte). Verwaltet
   die Deals-Engine (phase_128): Deal-Stammdaten + Verfügbarkeitsfenster,
   Preisgruppen (deal_groups + deal_group_items) und Add-on-Stufen (deal_addons +
   deal_addon_items).

   ARCHITEKTUR (wie Menü, Jonas-Entscheid §4/§6):
   - Lesen über die Lese-RPCs deals_overview() / deal_detail(id) (verschachteltes
     jsonb), NIE direkt aus einem Cache.
   - Schreiben DIREKT via window.App.supabase.from('<tabelle>') (RLS tenant +
     owner/admin/manager) — KEINE Schreib-RPCs. IDs client-seitig via uid().
   - Non-destruktive Renders: Skalar-Felder (Name/Preis/MwSt/Zeiten/Regeln) per
     onchange → DB-Write OHNE Tab-Re-Render (Fokus bleibt). NUR Struktur-Klicks
     (Gruppe/Item/Add-on hinzufügen/löschen, Aktiv-Toggle) rendern neu.
   - Inline-Handler als func() (CSP/dom-bindings, Memory gg-inline-handler-csp).

   Rechte: owner/admin/manager (operator-roles VIEW_MATRIX system-Gruppe;
   auth.canEdit('deals')). service/staff/lager sehen die System-Gruppe nicht.
============================================================================ */

import { htmlEscape, uid, showToast, requireEditPermission } from '../../utils/helpers.js';

const DAYS = [ { n: 1, l: 'Mo' }, { n: 2, l: 'Di' }, { n: 3, l: 'Mi' }, { n: 4, l: 'Do' }, { n: 5, l: 'Fr' }, { n: 6, l: 'Sa' }, { n: 7, l: 'So' } ];

const _state = {
  deals: [],          // deals_overview()
  menuItems: [],       // Menü zur Item-Zuordnung
  editing: null,       // deal_detail(id)-jsonb des aktuell bearbeiteten Deals
  loading: false
};

/* -------------------------------------------------------------------------- */
/* Infrastruktur                                                               */
/* -------------------------------------------------------------------------- */

function _supa() { return (window.App && window.App.supabase) || null; }
function _rid() {
  const a = window.App && window.App.auth;
  return (a && typeof a.getCurrentRestaurantIdSync === 'function' && a.getCurrentRestaurantIdSync())
    || (window.AppConfig && window.AppConfig.defaultRestaurantId) || null;
}
function _eur(cents) { return ((Number(cents) || 0) / 100).toFixed(2); }
function _toCents(v) { const n = parseFloat(String(v == null ? '' : v).replace(',', '.')); return Number.isFinite(n) ? Math.max(0, Math.round(n * 100)) : 0; }
function _hhmm(t) { return t ? String(t).slice(0, 5) : ''; }

async function _loadMenuItems() {
  try {
    const st = window.App && window.App.storage && window.App.storage._getInternalState && window.App.storage._getInternalState();
    if (st && Array.isArray(st.menu) && st.menu.length) return st.menu;
  } catch (_) { /* fallthrough */ }
  try {
    if (window.App && window.App.storage && window.App.storage.menu && typeof window.App.storage.menu.list === 'function') {
      return await window.App.storage.menu.list();
    }
  } catch (_) { /* ignore */ }
  return [];
}

function _menuName(id) {
  const m = _state.menuItems.find(x => x.id === id);
  return m ? (m.name || id) : id;
}

/* -------------------------------------------------------------------------- */
/* Laden + Top-Level-Render                                                    */
/* -------------------------------------------------------------------------- */

/** Entry: lädt Deals + Menü und rendert Liste oder Editor in #tab-deals. */
export async function renderDealsAdmin() {
  const root = document.getElementById('tab-deals');
  if (!root) return;
  if (!_state.menuItems.length) _state.menuItems = await _loadMenuItems();
  if (_state.editing) { _renderEditor(root); return; }

  root.innerHTML = `<div class="settings-card"><p class="hint">Lade Deals…</p></div>`;
  const supa = _supa();
  if (!supa) { root.innerHTML = `<div class="settings-card"><p class="hint">Keine Verbindung.</p></div>`; return; }
  try {
    const { data, error } = await supa.rpc('deals_overview');
    if (error) throw error;
    _state.deals = Array.isArray(data) ? data : [];
  } catch (e) {
    console.error('[deals] overview:', e);
    root.innerHTML = `<div class="settings-card"><p class="hint">Deals konnten nicht geladen werden: ${htmlEscape(e.message || String(e))}</p></div>`;
    return;
  }
  _renderList(root);
}

function _windowLabel(d) {
  const days = Array.isArray(d.available_days) && d.available_days.length
    ? d.available_days.slice().sort((a, b) => a - b).map(n => (DAYS.find(x => x.n === n) || {}).l || n).join('·')
    : 'jeden Tag';
  const from = _hhmm(d.available_from), to = _hhmm(d.available_to);
  const time = (from || to) ? `${from || '00:00'}–${to || '24:00'}` : 'ganztägig';
  return `${days} · ${time}${d.guest_only_window ? ' · nur Gäste' : ''}`;
}

function _renderList(root) {
  const rows = (_state.deals || []).map(d => `
    <tr>
      <td>
        <label class="deal-active-toggle" title="Aktiv">
          <input type="checkbox" ${d.active ? 'checked' : ''} onchange="dealsToggleActive('${htmlEscape(d.id)}', this.checked)">
        </label>
      </td>
      <td><strong>${htmlEscape(d.name)}</strong></td>
      <td><span class="hint">${htmlEscape(_windowLabel(d))}</span></td>
      <td class="num">${d.group_count} Gr. · ${d.addon_count} Add-on</td>
      <td class="deal-row-actions">
        <button class="btn btn-ghost btn-sm" onclick="dealsEditDeal('${htmlEscape(d.id)}')">Bearbeiten</button>
        <button class="btn btn-ghost btn-sm" onclick="dealsDeleteDeal('${htmlEscape(d.id)}')" title="Löschen">✕</button>
      </td>
    </tr>
  `).join('');

  root.innerHTML = `
    <div class="settings-card">
      <div class="settings-card-head">
        <div>
          <h3 style="margin:0;">Deals / Aktionen</h3>
          <p class="hint" style="margin:.2rem 0 0;">Special-Offers (Mittagstisch, Frühstück, Happy Hour). Ein Deal bündelt bestehende Gerichte zum Aktionspreis und zerfällt beim Bestellen in normale Positionen.</p>
        </div>
        <button class="btn btn-primary" onclick="dealsCreateDeal()">+ Deal anlegen</button>
      </div>
      ${(_state.deals || []).length === 0
        ? `<p class="hint" style="margin-top:14px;">Noch keine Deals angelegt.</p>`
        : `<table class="deals-table"><thead><tr><th>Aktiv</th><th>Name</th><th>Verfügbarkeit</th><th>Inhalt</th><th></th></tr></thead><tbody>${rows}</tbody></table>`}
    </div>
  `;
}

/* -------------------------------------------------------------------------- */
/* Editor                                                                      */
/* -------------------------------------------------------------------------- */

function _itemPickerHtml(assignedIds, onAdd) {
  const unassigned = _state.menuItems
    .filter(m => m.available !== false && !assignedIds.includes(m.id))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  const opts = unassigned.map(m => `<option value="${htmlEscape(m.id)}">${htmlEscape(m.name)}${m.category ? ' · ' + htmlEscape(m.category) : ''}</option>`).join('');
  return `
    <select class="deal-item-add" onchange="${onAdd}(this.value); this.selectedIndex=0;">
      <option value="">+ Gericht hinzufügen…</option>
      ${opts}
    </select>`;
}

function _chipsHtml(items, onRemove) {
  if (!items || !items.length) return `<span class="hint">— noch keine Gerichte zugeordnet —</span>`;
  return items.map(it => `
    <span class="deal-chip">
      ${htmlEscape(_menuName(it.menu_item_id))}
      <button type="button" class="deal-chip-x" onclick="${onRemove}('${htmlEscape(it.id)}')" title="Entfernen">✕</button>
    </span>`).join('');
}

function _groupCardHtml(g) {
  const rules = g.rules || {};
  return `
    <div class="deal-subcard" data-group="${htmlEscape(g.id)}">
      <div class="deal-subcard-head">
        <input type="text" class="deal-grp-name" value="${htmlEscape(g.name || '')}" placeholder="Gruppenname (z. B. Burger 10 €)"
               onfocus="this.select()" onchange="dealsUpdateGroup('${htmlEscape(g.id)}','name', this.value)">
        <button class="btn btn-ghost btn-sm" onclick="dealsDeleteGroup('${htmlEscape(g.id)}')" title="Gruppe löschen">✕</button>
      </div>
      <div class="deal-field-row">
        <label>Deal-Preis (€)
          <input type="number" step="0.01" min="0" value="${_eur(g.deal_price_cents)}"
                 onchange="dealsUpdateGroup('${htmlEscape(g.id)}','deal_price_cents', this.value)">
        </label>
        <label>MwSt
          <select onchange="dealsUpdateGroup('${htmlEscape(g.id)}','tax_rate', this.value)">
            <option value="7" ${Number(g.tax_rate) === 7 ? 'selected' : ''}>7 % (Essen)</option>
            <option value="19" ${Number(g.tax_rate) === 19 ? 'selected' : ''}>19 % (Getränk)</option>
          </select>
        </label>
      </div>
      <div class="deal-field-row deal-rules">
        <label class="deal-check"><input type="checkbox" ${rules.sauces_included ? 'checked' : ''} onchange="dealsUpdateGroupRule('${htmlEscape(g.id)}','sauces_included', this.checked)"> Soßen inkl.</label>
        <label class="deal-check"><input type="checkbox" ${rules.customizable ? 'checked' : ''} onchange="dealsUpdateGroupRule('${htmlEscape(g.id)}','customizable', this.checked)"> Individualisierbar</label>
        <label>Topping-Gruppe
          <input type="text" value="${htmlEscape(rules.topping_group || '')}" placeholder="z. B. Rind/Hähnchen/Spicy Bean/Beyond"
                 onchange="dealsUpdateGroupRule('${htmlEscape(g.id)}','topping_group', this.value)">
        </label>
        <label>Brot-Whitelist
          <input type="text" value="${htmlEscape((rules.bun_whitelist || []).join(', '))}" placeholder="z. B. Brioche, glutenfrei"
                 onchange="dealsUpdateGroupRule('${htmlEscape(g.id)}','bun_whitelist', this.value)">
        </label>
      </div>
      <div class="deal-items">
        <div class="deal-chips">${_chipsHtml(g.items, 'dealsRemoveGroupItem')}</div>
        ${_itemPickerHtml((g.items || []).map(i => i.menu_item_id), `dealsAddGroupItem_${htmlEscape(g.id)}`)}
      </div>
    </div>`;
}

function _addonCardHtml(a) {
  const kindLabel = a.kind === 'drink' ? '🥤 Drink' : '🍟 Beilage';
  return `
    <div class="deal-subcard" data-addon="${htmlEscape(a.id)}">
      <div class="deal-subcard-head">
        <span class="deal-addon-kind">${kindLabel}</span>
        <input type="text" class="deal-grp-name" value="${htmlEscape(a.label || '')}" placeholder="Stufen-Label (z. B. Beilage 3 €)"
               onfocus="this.select()" onchange="dealsUpdateAddon('${htmlEscape(a.id)}','label', this.value)">
        <button class="btn btn-ghost btn-sm" onclick="dealsDeleteAddon('${htmlEscape(a.id)}')" title="Stufe löschen">✕</button>
      </div>
      <div class="deal-field-row">
        <label>Aufpreis (€)
          <input type="number" step="0.01" min="0" value="${_eur(a.price_cents)}"
                 onchange="dealsUpdateAddon('${htmlEscape(a.id)}','price_cents', this.value)">
        </label>
        <label>MwSt
          <select onchange="dealsUpdateAddon('${htmlEscape(a.id)}','tax_rate', this.value)">
            <option value="7" ${Number(a.tax_rate) === 7 ? 'selected' : ''}>7 % (Essen)</option>
            <option value="19" ${Number(a.tax_rate) === 19 ? 'selected' : ''}>19 % (Getränk)</option>
          </select>
        </label>
      </div>
      <div class="deal-items">
        <div class="deal-chips">${_chipsHtml(a.items, 'dealsRemoveAddonItem')}</div>
        ${_itemPickerHtml((a.items || []).map(i => i.menu_item_id), `dealsAddAddonItem_${htmlEscape(a.id)}`)}
      </div>
    </div>`;
}

function _renderEditor(root) {
  const d = _state.editing;
  if (!d) { renderDealsAdmin(); return; }
  // Pro Gruppe/Add-on einen eindeutigen Add-Handler auf window legen (CSP: func()).
  (d.groups || []).forEach(g => { window['dealsAddGroupItem_' + g.id] = (mid) => dealsAddGroupItem(g.id, mid); });
  (d.addons || []).forEach(a => { window['dealsAddAddonItem_' + a.id] = (mid) => dealsAddAddonItem(a.id, mid); });

  const daysSel = new Set(Array.isArray(d.available_days) ? d.available_days : []);
  const dayChecks = DAYS.map(day => `
    <label class="deal-day ${daysSel.has(day.n) ? 'on' : ''}">
      <input type="checkbox" ${daysSel.has(day.n) ? 'checked' : ''} onchange="dealsToggleDay(${day.n}, this.checked)"> ${day.l}
    </label>`).join('');

  root.innerHTML = `
    <div class="settings-card">
      <div class="deal-editor-head">
        <button class="btn btn-ghost btn-sm" onclick="dealsBackToList()">← Alle Deals</button>
        <input type="text" class="deal-name-input" value="${htmlEscape(d.name || '')}" placeholder="Deal-Name"
               onfocus="this.select()" onchange="dealsUpdateDeal('name', this.value)">
        <label class="deal-active-inline"><input type="checkbox" ${d.active ? 'checked' : ''} onchange="dealsUpdateDeal('active', this.checked)"> Aktiv</label>
      </div>

      <div class="deal-section">
        <h4>Verfügbarkeit</h4>
        <div class="deal-days">${dayChecks}</div>
        <div class="deal-field-row">
          <label>von <input type="time" value="${_hhmm(d.available_from)}" onchange="dealsUpdateDeal('available_from', this.value)"></label>
          <label>bis <input type="time" value="${_hhmm(d.available_to)}" onchange="dealsUpdateDeal('available_to', this.value)"></label>
          <label class="deal-check"><input type="checkbox" ${d.guest_only_window ? 'checked' : ''} onchange="dealsUpdateDeal('guest_only_window', this.checked)"> Fenster nur für Gäste (Cockpit immer)</label>
        </div>
        <p class="hint">Leere Tage/Zeiten = unbeschränkt. „nur Gäste": das Cockpit kann den Deal jederzeit bestellen.</p>
      </div>

      <div class="deal-section">
        <div class="deal-section-head"><h4>Preisgruppen</h4><button class="btn btn-ghost btn-sm" onclick="dealsAddGroup()">+ Preisgruppe</button></div>
        ${(d.groups || []).length ? (d.groups || []).map(_groupCardHtml).join('') : '<p class="hint">Noch keine Gruppe. Eine Gruppe = ein Aktionspreis (z. B. „Burger 10 €") mit zugeordneten Gerichten.</p>'}
      </div>

      <div class="deal-section">
        <div class="deal-section-head"><h4>Add-on-Stufen (optional)</h4>
          <span>
            <button class="btn btn-ghost btn-sm" onclick="dealsAddAddon('side')">+ Beilage</button>
            <button class="btn btn-ghost btn-sm" onclick="dealsAddAddon('drink')">+ Drink</button>
          </span>
        </div>
        ${(d.addons || []).length ? (d.addons || []).map(_addonCardHtml).join('') : '<p class="hint">Keine Add-ons. Beilage/Drink sind optionale Aufpreis-Stufen.</p>'}
      </div>
    </div>
  `;
}

/* -------------------------------------------------------------------------- */
/* Daten-Reload                                                                */
/* -------------------------------------------------------------------------- */

async function _reloadEditing() {
  const supa = _supa(); if (!supa || !_state.editing) return;
  const id = _state.editing.id;
  try {
    const { data, error } = await supa.rpc('deal_detail', { p_deal_id: id });
    if (error) throw error;
    _state.editing = data || _state.editing;
  } catch (e) { console.error('[deals] deal_detail:', e); showToast('Deal konnte nicht neu geladen werden.', 'error'); }
  const root = document.getElementById('tab-deals');
  if (root) _renderEditor(root);
}

/* -------------------------------------------------------------------------- */
/* Handler — Liste                                                             */
/* -------------------------------------------------------------------------- */

export async function dealsCreateDeal() {
  if (!requireEditPermission('deals', 'Deal anlegen')) return;
  const supa = _supa(); const rid = _rid();
  if (!supa || !rid) { showToast('Keine Verbindung.', 'error'); return; }
  const id = uid('deal');
  try {
    const { error } = await supa.from('deals').insert({
      id, restaurant_id: rid, name: 'Neuer Deal', active: false, sort_order: (_state.deals.length + 1) * 10,
      available_days: null, available_from: null, available_to: null, guest_only_window: true
    });
    if (error) throw error;
  } catch (e) { console.error('[deals] create:', e); showToast('Anlegen fehlgeschlagen: ' + (e.message || e), 'error'); return; }
  await dealsEditDeal(id);
}

export async function dealsEditDeal(id) {
  const supa = _supa(); if (!supa) return;
  try {
    const { data, error } = await supa.rpc('deal_detail', { p_deal_id: id });
    if (error) throw error;
    _state.editing = data;
  } catch (e) { console.error('[deals] edit:', e); showToast('Deal konnte nicht geöffnet werden.', 'error'); return; }
  const root = document.getElementById('tab-deals');
  if (root) _renderEditor(root);
}

export function dealsBackToList() {
  _state.editing = null;
  renderDealsAdmin();
}

export async function dealsDeleteDeal(id) {
  if (!requireEditPermission('deals', 'Deal löschen')) return;
  const deal = _state.deals.find(d => d.id === id);
  if (!(await window.styledConfirm({ title: `Deal „${(deal && deal.name) || id}" löschen?`, body: 'Inkl. Gruppen und Add-ons.', danger: true, confirmLabel: 'Löschen' })).confirmed) return;
  const supa = _supa(); if (!supa) return;
  try {
    // P2-Fix: HARTES Löschen — FK ON DELETE CASCADE räumt deal_groups/deal_group_items/
    // deal_addons/deal_addon_items mit ab (keine verwaisten Zeilen). Deal-Config ist
    // nicht order-/GoBD-referenziert (orders tragen nur menu_item_id + customizations.deal.id
    // als String-Marker, keinen FK) → hartes Löschen unkritisch.
    const { error } = await supa.from('deals').delete().eq('id', id);
    if (error) throw error;
  } catch (e) { console.error('[deals] delete:', e); showToast('Löschen fehlgeschlagen: ' + (e.message || e), 'error'); return; }
  showToast('Deal gelöscht.', 'info');
  renderDealsAdmin();
}

export async function dealsToggleActive(id, checked) {
  if (!requireEditPermission('deals', 'Deal aktivieren')) { renderDealsAdmin(); return; }
  const supa = _supa(); if (!supa) return;
  try {
    const { error } = await supa.from('deals').update({ active: !!checked, updated_at: new Date().toISOString() }).eq('id', id);
    if (error) throw error;
    const d = _state.deals.find(x => x.id === id); if (d) d.active = !!checked;
  } catch (e) { console.error('[deals] toggle:', e); showToast('Konnte nicht umgeschaltet werden.', 'error'); renderDealsAdmin(); }
}

/* -------------------------------------------------------------------------- */
/* Handler — Editor Stammdaten (Skalar: kein Re-Render)                        */
/* -------------------------------------------------------------------------- */

async function _patchDeal(patch) {
  const supa = _supa(); if (!supa || !_state.editing) return;
  patch.updated_at = new Date().toISOString();
  // P2-Fix (Tag-Verlust): lokalen State SOFORT (vor dem await) aktualisieren, sonst
  // lesen schnell aufeinanderfolgende Handler (z.B. mehrere Tag-Checkboxen) noch den
  // alten available_days-Wert → letzter DB-Write gewinnt, gesetzte Tage gehen verloren.
  Object.assign(_state.editing, patch);
  try {
    const { error } = await supa.from('deals').update(patch).eq('id', _state.editing.id);
    if (error) throw error;
  } catch (e) { console.error('[deals] patchDeal:', e); showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error'); }
}

export function dealsUpdateDeal(key, val) {
  if (!requireEditPermission('deals', 'Deal ändern')) return;
  let v = val;
  if (key === 'active' || key === 'guest_only_window') v = !!val;
  if ((key === 'available_from' || key === 'available_to') && !val) v = null;
  _patchDeal({ [key]: v });
}

export function dealsToggleDay(day, checked) {
  if (!requireEditPermission('deals', 'Deal ändern')) return;
  const cur = new Set(Array.isArray(_state.editing.available_days) ? _state.editing.available_days : []);
  if (checked) cur.add(Number(day)); else cur.delete(Number(day));
  const arr = Array.from(cur).sort((a, b) => a - b);
  _patchDeal({ available_days: arr.length ? arr : null });
  // Tag-Pille nur lokal markieren (kein Re-Render): Klassen-Toggle am Label.
  try {
    const root = document.getElementById('tab-deals');
    const lbl = root && root.querySelectorAll('.deal-day')[Number(day) - 1];
    if (lbl) lbl.classList.toggle('on', !!checked);
  } catch (_) { /* tolerant */ }
}

/* -------------------------------------------------------------------------- */
/* Handler — Gruppen                                                           */
/* -------------------------------------------------------------------------- */

export async function dealsAddGroup() {
  if (!requireEditPermission('deals', 'Preisgruppe anlegen')) return;
  const supa = _supa(); const rid = _rid(); if (!supa || !rid || !_state.editing) return;
  try {
    const { error } = await supa.from('deal_groups').insert({
      id: uid('dg'), deal_id: _state.editing.id, restaurant_id: rid,
      name: 'Neue Gruppe', deal_price_cents: 1000, tax_rate: 7, rules: {}, sort_order: (_state.editing.groups || []).length * 10
    });
    if (error) throw error;
  } catch (e) { console.error('[deals] addGroup:', e); showToast('Gruppe anlegen fehlgeschlagen: ' + (e.message || e), 'error'); return; }
  await _reloadEditing();
}

export async function dealsUpdateGroup(groupId, key, val) {
  if (!requireEditPermission('deals', 'Gruppe ändern')) return;
  const supa = _supa(); if (!supa) return;
  const patch = {};
  if (key === 'deal_price_cents') patch.deal_price_cents = _toCents(val);
  else if (key === 'tax_rate') patch.tax_rate = Number(val) === 19 ? 19 : 7;
  else patch[key] = val;
  const g0 = (_state.editing.groups || []).find(x => x.id === groupId); if (g0) Object.assign(g0, patch);
  try {
    const { error } = await supa.from('deal_groups').update(patch).eq('id', groupId);
    if (error) throw error;
  } catch (e) { console.error('[deals] updateGroup:', e); showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error'); }
}

export async function dealsUpdateGroupRule(groupId, ruleKey, val) {
  if (!requireEditPermission('deals', 'Gruppe ändern')) return;
  const supa = _supa(); if (!supa) return;
  const g = (_state.editing.groups || []).find(x => x.id === groupId); if (!g) return;
  const rules = { ...(g.rules || {}) };
  if (ruleKey === 'sauces_included' || ruleKey === 'customizable') rules[ruleKey] = !!val;
  else if (ruleKey === 'bun_whitelist') rules.bun_whitelist = String(val).split(',').map(s => s.trim()).filter(Boolean);
  else rules[ruleKey] = (val || '').trim ? (val || '').trim() : val;
  g.rules = rules;   // optimistisch vor dem await (Race-Schutz wie _patchDeal)
  try {
    const { error } = await supa.from('deal_groups').update({ rules }).eq('id', groupId);
    if (error) throw error;
  } catch (e) { console.error('[deals] updateGroupRule:', e); showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error'); }
}

export async function dealsDeleteGroup(groupId) {
  if (!requireEditPermission('deals', 'Gruppe löschen')) return;
  const supa = _supa(); if (!supa) return;
  try { const { error } = await supa.from('deal_groups').delete().eq('id', groupId); if (error) throw error; }
  catch (e) { console.error('[deals] delGroup:', e); showToast('Löschen fehlgeschlagen: ' + (e.message || e), 'error'); return; }
  await _reloadEditing();
}

export async function dealsAddGroupItem(groupId, menuItemId) {
  if (!menuItemId) return;
  if (!requireEditPermission('deals', 'Gericht zuordnen')) return;
  const supa = _supa(); const rid = _rid(); if (!supa || !rid) return;
  try {
    const { error } = await supa.from('deal_group_items').insert({ id: uid('dgi'), deal_group_id: groupId, restaurant_id: rid, menu_item_id: menuItemId, sort_order: 0 });
    if (error) throw error;
  } catch (e) { console.error('[deals] addGroupItem:', e); showToast('Zuordnen fehlgeschlagen: ' + (e.message || e), 'error'); return; }
  await _reloadEditing();
}

export async function dealsRemoveGroupItem(groupItemId) {
  if (!requireEditPermission('deals', 'Gericht entfernen')) return;
  const supa = _supa(); if (!supa) return;
  try { const { error } = await supa.from('deal_group_items').delete().eq('id', groupItemId); if (error) throw error; }
  catch (e) { console.error('[deals] rmGroupItem:', e); showToast('Entfernen fehlgeschlagen: ' + (e.message || e), 'error'); return; }
  await _reloadEditing();
}

/* -------------------------------------------------------------------------- */
/* Handler — Add-ons                                                           */
/* -------------------------------------------------------------------------- */

export async function dealsAddAddon(kind) {
  if (!requireEditPermission('deals', 'Add-on anlegen')) return;
  const supa = _supa(); const rid = _rid(); if (!supa || !rid || !_state.editing) return;
  const isDrink = kind === 'drink';
  try {
    const { error } = await supa.from('deal_addons').insert({
      id: uid('da'), deal_id: _state.editing.id, restaurant_id: rid, kind: isDrink ? 'drink' : 'side',
      label: isDrink ? 'Drink 2 €' : 'Beilage 3 €', price_cents: isDrink ? 200 : 300, tax_rate: isDrink ? 19 : 7,
      sort_order: (_state.editing.addons || []).length * 10
    });
    if (error) throw error;
  } catch (e) { console.error('[deals] addAddon:', e); showToast('Add-on anlegen fehlgeschlagen: ' + (e.message || e), 'error'); return; }
  await _reloadEditing();
}

export async function dealsUpdateAddon(addonId, key, val) {
  if (!requireEditPermission('deals', 'Add-on ändern')) return;
  const supa = _supa(); if (!supa) return;
  const patch = {};
  if (key === 'price_cents') patch.price_cents = _toCents(val);
  else if (key === 'tax_rate') patch.tax_rate = Number(val) === 19 ? 19 : 7;
  else patch[key] = val;
  const a0 = (_state.editing.addons || []).find(x => x.id === addonId); if (a0) Object.assign(a0, patch);
  try {
    const { error } = await supa.from('deal_addons').update(patch).eq('id', addonId);
    if (error) throw error;
  } catch (e) { console.error('[deals] updateAddon:', e); showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error'); }
}

export async function dealsDeleteAddon(addonId) {
  if (!requireEditPermission('deals', 'Add-on löschen')) return;
  const supa = _supa(); if (!supa) return;
  try { const { error } = await supa.from('deal_addons').delete().eq('id', addonId); if (error) throw error; }
  catch (e) { console.error('[deals] delAddon:', e); showToast('Löschen fehlgeschlagen: ' + (e.message || e), 'error'); return; }
  await _reloadEditing();
}

export async function dealsAddAddonItem(addonId, menuItemId) {
  if (!menuItemId) return;
  if (!requireEditPermission('deals', 'Gericht zuordnen')) return;
  const supa = _supa(); const rid = _rid(); if (!supa || !rid) return;
  try {
    const { error } = await supa.from('deal_addon_items').insert({ id: uid('dai'), deal_addon_id: addonId, restaurant_id: rid, menu_item_id: menuItemId, sort_order: 0 });
    if (error) throw error;
  } catch (e) { console.error('[deals] addAddonItem:', e); showToast('Zuordnen fehlgeschlagen: ' + (e.message || e), 'error'); return; }
  await _reloadEditing();
}

export async function dealsRemoveAddonItem(addonItemId) {
  if (!requireEditPermission('deals', 'Gericht entfernen')) return;
  const supa = _supa(); if (!supa) return;
  try { const { error } = await supa.from('deal_addon_items').delete().eq('id', addonItemId); if (error) throw error; }
  catch (e) { console.error('[deals] rmAddonItem:', e); showToast('Entfernen fehlgeschlagen: ' + (e.message || e), 'error'); return; }
  await _reloadEditing();
}

/* Test-Hook */
export function _resetForTest() { _state.deals = []; _state.menuItems = []; _state.editing = null; _state.loading = false; }

/* -------------------------------------------------------------------------- */
/* window-Bridge (CSP: inline func())                                          */
/* -------------------------------------------------------------------------- */
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderDealsAdmin,
    dealsCreateDeal, dealsEditDeal, dealsBackToList, dealsDeleteDeal, dealsToggleActive,
    dealsUpdateDeal, dealsToggleDay,
    dealsAddGroup, dealsUpdateGroup, dealsUpdateGroupRule, dealsDeleteGroup, dealsAddGroupItem, dealsRemoveGroupItem,
    dealsAddAddon, dealsUpdateAddon, dealsDeleteAddon, dealsAddAddonItem, dealsRemoveAddonItem
  });
}
